<template>
  <div v-for="(option, index) in options" :key="index">
    <SingleSelectOption
      v-if="option.type == 'single_select'"
      :option="option"
      :selected-options-ids="selectedOptionsValuesIds"
      @click="onSingleSelectOptionClicked"
    />
    <CounterOption
      v-if="option.type == 'counter'"
      :values="getFilteredValues(option)"
      :maximum="MAX_INDIVIDUAL_TICKETS"
      :max-error-message="`Cannot add more. Individual tickets max is ${MAX_INDIVIDUAL_TICKETS}.`"
      @change="
        (optionValueId, newValue) => onCounterQuantityChanged(option, optionValueId, newValue)
      "
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useProductStore } from '@/stores/product'
import { useOrderStore } from '@/stores/order'
import SingleSelectOption from '@/components/SingleSelectOption.vue'
import CounterOption from '@/components/CounterOption.vue'
import { MAX_INDIVIDUAL_TICKETS } from '@/utils/constants'

const productId = import.meta.env.VITE_TICKETS_PRODUCT_ID
const productStore = useProductStore()
const orderStore = useOrderStore()
const options = ref<ProductOption[]>([])
const variants = ref<ProductVariant[]>([])
const selectedOptionsValues = ref<any>({})
const selectedOptionsValuesIds = computed(() => optionValuesToKeys(selectedOptionsValues.value))

async function loadProduct() {
  const response = await productStore.getProduct(productId)

  if (!response.error) {
    options.value = response.options.sort((val1, val2) => val1.priority - val2.priority)
    variants.value = response.variants
    initSelectedOptionsValues()
    verifyProductVariantSelection()
  }
}

function onSingleSelectOptionClicked(option: ProductOption, optionValueId: string) {
  selectedOptionsValues.value[option.value] = { [optionValueId]: 1 }
  verifyProductVariantSelection()
}

function onCounterQuantityChanged(option: ProductOption, optionValueId: string, quantity: number) {
  if (quantity > 0) {
    selectedOptionsValues.value[option.value][optionValueId] = quantity
  } else {
    delete selectedOptionsValues.value[option.value][optionValueId]
  }
  verifyProductVariantSelection()
}

function verifyProductVariantSelection() {
  const orderItems: OrderItem[] = []

  variants.value.forEach((variant: ProductVariant) => {
    const variantOptionValues = [...variant.key]
    let selectedQuantity = 0

    Object.values(selectedOptionsValues.value).forEach((optionValue: any) => {
      for (const [key, value] of Object.entries(optionValue)) {
        const index = variantOptionValues.indexOf(key)

        if (index != -1) {
          variantOptionValues.splice(index, 1)
          if (typeof value == 'number' && value) selectedQuantity = value
        }
      }
    })

    if (variantOptionValues.length == 0) {
      orderItems.push({
        variant_id: variant.id,
        descriptions: variant.descriptions,
        price: variant.price,
        quantity: selectedQuantity
      })
    }
  })

  orderStore.items = orderItems
}

function initSelectedOptionsValues() {
  options.value.forEach((option) => {
    selectedOptionsValues.value[option.value] = {}
    option.values.forEach((value) => {
      if (value.selected) selectedOptionsValues.value[option.value][value.id] = 1
    })
  })
}

function optionValuesToKeys(optionValues: any) {
  return Object.values(optionValues).flatMap((optionValue: any) => Object.keys(optionValue))
}

function getVariantByOptionKeys(optionKeys: string[]) {
  return variants.value.find((variant) => variant.key.every((key) => optionKeys.includes(key)))
}

function getFilteredValues(option: ProductOption) {
  return option.values.filter((value) => {
    const values = { ...selectedOptionsValues.value, [option.value]: { [value.id]: 1 } }
    return getVariantByOptionKeys(optionValuesToKeys(values))
  })
}

loadProduct()
</script>

<style scoped></style>
